import { el, btn, backButton } from '@/ui/dom';
import { nativeShims } from '@/native/shims';
import { LobbyStage, type SeatTarget } from '@/ui/lobby/LobbyStage';
import { VEHICLE_ORDER } from '@/entities/vehicles';
import { ACCENTS, STAT_BARS } from '@/ui/vehicleCatalog';
import { tuning } from '@/config/tuning';
import { onlineRacerName } from '@/config/racers';
import { displayRacerName, onBlockedChange } from '@/social/moderation';
import { REPORT_FLAG_SVG } from '@/ui/social/playerSheet';
import type { LobbySeat } from '@/net/lobby';
import { LOBBY_MIN_TO_START } from '@/net/lobby';
import { sfxCollect } from '@/audio/sfx';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';

/**
 * RaceLobbyScreen.ts — the online RACE LOBBY (M63; redesigned M64 to the
 * owner's reference art). Full-bleed showroom: a photoreal neon-garage
 * backdrop (public/lobby/bg.webp — swappable art) under a transparent 3D
 * stage where every racer's ride rotates over its livery ring; the LOCAL car
 * is the big hero, front-left, with the rest receding to the right. Per-seat
 * header columns carry the grid number / racer name / ride (in its livery
 * colour) / a glowing ✓ READY; the bottom bar holds the VEHICLE STATS panel,
 * the YOUR RIDE picker (arrows + dot rail) and the big LOCK IN button.
 *
 * M64 also moves MATCHMAKING INTO the lobby: Quick Match / Start a Tour open
 * this screen immediately in "searching" mode ("Searching for racers near
 * you…", seats filling as racers are found) — the player picks a ride while
 * the matchmaker works; the match then connects in place.
 *
 * The header columns are the layout truth: their measured centres feed
 * LobbyStage.applyTargets so the cars line up under their plates at any
 * window shape (desktop, landscape phone, portrait).
 */

export interface RaceLobbyCallbacks {
  /** ◀ ▶: the (not yet locked) selection changed — mirrored to the room live. */
  onCycleVehicle: (vehicleId: string) => void;
  /** LOCK IN pressed — this ride is final, we're READY. */
  onLockIn: (vehicleId: string) => void;
  /** START RACE / START TOUR pressed (friend rooms; server gates on all-ready). */
  onRequestStart: () => void;
  onCancel: () => void;
  /** M81: a vehicle's equipped cosmetics as a serialized loadout string — MY
   *  showroom car wears it while cycling (other seats' looks ride the roster). */
  lookFor: (vehicleId: string) => string;
  /** M156: a racer's name was tapped — open their REPORT / BLOCK card. Gets
   *  the REAL name (as the room relayed it), never the masked label. */
  onPlayerName?: (name: string) => void;
  /** M156: REPORT A RACER — choose from everyone else in the room. */
  onReportRacers?: (names: string[]) => void;
}

export interface RaceLobbyShowOpts {
  /** M140: 'crew' = a crew race (no room code panel; invites went out to the
   *  crew). A PUBLIC crew race starts through FIND RACERS (crewPublic). */
  mode: 'friend' | 'matchmade' | 'crew';
  /** M140: the crew's name (crew mode). */
  crewName?: string;
  /** M140: an open crew race — FIND RACERS fills the empty seats. */
  crewPublic?: boolean;
  /** Labels the start button/status ("RACE" vs "TOUR"). */
  tour: boolean;
  /** Grid size if already known (the creator knows; a joiner learns on welcome). */
  fieldSize?: number;
  /** Friend rooms: the shareable code + link. */
  code?: string;
  link?: string;
  /** "Tokyo · 4 racers" — refreshed from the welcome. */
  info?: string;
  /** The rides this player may pick (unlocked, garage order) + the initial one. */
  vehicles: string[];
  initialVehicle: string;
  /** Seeds the deterministic name pool (the room code; matchmaking learns it late). */
  roomKey: string;
}

const vehicleLabel = (id: string): string => VEHICLE_ORDER.find((v) => v.id === id)?.label ?? id;

interface SeatCol {
  col: HTMLDivElement;
  num: HTMLDivElement;
  name: HTMLDivElement;
  veh: HTMLDivElement;
  state: HTMLDivElement;
}

export class RaceLobbyScreen {
  private root: HTMLDivElement;
  private stageHost: HTMLDivElement;
  private stage: LobbyStage | null = null;
  private seatsRow: HTMLDivElement;
  private infoEl: HTMLDivElement;
  private titleEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private reconnectEl: HTMLDivElement;
  private vehNameEl: HTMLDivElement;
  private dotsEl: HTMLDivElement;
  private statsRows: { fill: HTMLSpanElement }[] = [];
  private statsPanel: HTMLDivElement;
  private prevBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private lockBtn: HTMLButtonElement;
  private lockedChip: HTMLDivElement;
  private startBtn: HTMLButtonElement;
  private invitePanel: HTMLDivElement;
  private inviteLabel: HTMLDivElement;
  private codeEl: HTMLDivElement;
  private copyBtn: HTMLButtonElement;
  private copyResetTimer: number | null = null;

  private opts: RaceLobbyShowOpts | null = null;
  private link = '';
  private field = 0;
  private localSlot = -1;
  private selIndex = 0;
  private locked = false;
  private startRequested = false;
  private filledCount = 0;
  /** M64 in-lobby matchmaking: true while the matchmaker is still searching. */
  private searching = false;
  /** M140: a public crew room's search state + when its window closes. */
  private crewSearch: 'off' | 'on' | 'done' = 'off';
  private crewSearchEnds = 0;
  private crewTick: number | null = null;
  private searchFound = 1;
  private seatCols: SeatCol[] = [];
  /** Latest roster (re-rendered when the local slot / search state changes). */
  private lastSeats: LobbySeat[] = [];
  /** M156: each visible column's racer, by real name (null = me / empty). */
  private seatRawNames: (string | null)[] = [];
  private reportLink: HTMLButtonElement;
  private unsubBlocked: (() => void) | null = null;
  private unsubViewport: ViewportUnsubscribe | null = null;
  private readonly onWindowResize = (): void => {
    this.stage?.resize();
    this.layoutStage();
  };

  constructor(
    parent: HTMLElement,
    private cb: RaceLobbyCallbacks,
  ) {
    this.root = el('div', 'screen screen-racelobby', parent);
    this.root.style.display = 'none';

    // Full-bleed 3D stage behind everything (transparent canvas over the
    // photoreal backdrop, which is this root's CSS background).
    this.stageHost = el('div', 'racelobby-stage', this.root);

    // Top strip: leave + title on the left, invite chip on the right.
    const top = el('div', 'racelobby-top', this.root);
    const titles = el('div', 'racelobby-titles', top);
    const titleRow = el('div', 'racelobby-titlerow', titles);
    // M114b: the shared back chevron — but NO swipe here. This "back" LEAVES
    // a live multiplayer room; an accidental edge swipe must never disconnect
    // you from a lobby full of friends. The tap stays.
    backButton(titleRow, () => this.cb.onCancel(), { corner: false, swipe: false, label: 'Leave' });
    this.titleEl = el('div', 'racelobby-title', titleRow);
    this.infoEl = el('div', 'racelobby-info', titles);
    // M156 (App Store 1.2): REPORT A RACER — every name in this room can be
    // reported or blocked (a tap on a seat's name opens that racer directly).
    this.reportLink = el('button', 'mod-report-link racelobby-report', titles);
    this.reportLink.type = 'button';
    el('span', 'mod-link-ic', this.reportLink).innerHTML = REPORT_FLAG_SVG;
    el('span', '', this.reportLink).textContent = 'REPORT A RACER';
    this.reportLink.style.display = 'none'; // until someone else is in the room
    this.reportLink.addEventListener('click', () => {
      const names = this.seatRawNames.filter((n): n is string => !!n);
      this.cb.onReportRacers?.(names);
    });

    this.invitePanel = el('div', 'racelobby-invite', top);
    this.inviteLabel = el('div', 'racelobby-invite-label', this.invitePanel);
    this.inviteLabel.textContent = 'ROOM CODE';
    this.codeEl = el('div', 'racelobby-code', this.invitePanel);
    this.copyBtn = btn(
      'COPY INVITE LINK',
      'btn btn-secondary racelobby-copy',
      this.invitePanel,
      () => {
        void this.copy();
      },
    );

    // Per-seat header columns (the layout truth the 3D cars align to).
    this.seatsRow = el('div', 'racelobby-seats', this.root);

    // Bottom bar: stats | picker + status | actions.
    const bottom = el('div', 'racelobby-bottom', this.root);

    this.statsPanel = el('div', 'racelobby-statspanel', bottom);
    el('div', 'racelobby-stats-title', this.statsPanel).textContent = 'VEHICLE STATS';
    const labels = ['SPEED', 'ACCELERATION', 'HANDLING', 'WEIGHT'];
    STAT_BARS.forEach((s, i) => {
      const row = el('div', 'racelobby-stat', this.statsPanel);
      el('span', 'racelobby-stat-label', row).textContent = labels[i] ?? s.label;
      const track = el('span', 'racelobby-stat-track', row);
      const fill = el('span', 'racelobby-stat-fill', track);
      this.statsRows.push({ fill });
    });

    const centre = el('div', 'racelobby-centre', bottom);
    const picker = el('div', 'racelobby-picker', centre);
    this.prevBtn = btn('‹', 'btn racelobby-arrow', picker, () => this.cycle(-1));
    const vehWrap = el('div', 'racelobby-veh', picker);
    el('div', 'racelobby-veh-label', vehWrap).textContent = 'YOUR RIDE';
    this.vehNameEl = el('div', 'racelobby-veh-name', vehWrap);
    this.dotsEl = el('div', 'racelobby-dots', vehWrap);
    this.nextBtn = btn('›', 'btn racelobby-arrow', picker, () => this.cycle(1));
    this.statusEl = el('div', 'racelobby-status', centre);

    const actions = el('div', 'racelobby-actions', bottom);
    this.startBtn = btn('START RACE', 'btn racelobby-start', actions, () => {
      this.cb.onRequestStart();
    });
    this.lockBtn = btn('', 'btn racelobby-lock', actions, () => this.lockIn());
    this.lockBtn.innerHTML =
      '<span>LOCK IN<br>READY</span><span class="racelobby-lock-chev">›</span>';
    this.lockedChip = el('div', 'racelobby-lockedchip', actions);
    this.lockedChip.innerHTML = '✓ READY';

    this.reconnectEl = el('div', 'racelobby-reconnect', this.root);
    this.reconnectEl.textContent = 'RECONNECTING…';

    this.unsubViewport = subscribeViewport(this.onWindowResize);
    // M156: blocking someone repaints their column at once.
    this.unsubBlocked = onBlockedChange(() => {
      if (this.root.style.display !== 'none') this.renderSeats();
    });
  }

  show(opts: RaceLobbyShowOpts): void {
    this.opts = opts;
    this.link = opts.link ?? '';
    this.locked = false;
    this.startRequested = false;
    this.filledCount = 0;
    this.localSlot = -1;
    this.searching = false;
    this.searchFound = 1;
    this.lastSeats = [];
    this.selIndex = Math.max(0, opts.vehicles.indexOf(opts.initialVehicle));
    this.clearCopyTimer();
    this.copyBtn.textContent = 'COPY INVITE LINK';

    this.crewSearch = 'off';
    this.crewSearchEnds = 0;
    this.stopCrewTick();
    const crew = opts.mode === 'crew';
    this.paintTitle();
    this.infoEl.textContent = opts.info ?? '';
    // Friend rooms show the shareable code; a crew race shows the crew (its
    // invites already went out — nothing to copy).
    this.invitePanel.style.display = (opts.mode === 'friend' && opts.code) || crew ? '' : 'none';
    this.invitePanel.classList.toggle('racelobby-crewpanel', crew);
    this.inviteLabel.textContent = crew ? 'YOUR CLUB' : 'ROOM CODE';
    this.codeEl.textContent = crew ? (opts.crewName ?? '').toUpperCase() : (opts.code ?? '');
    this.copyBtn.style.display = crew ? 'none' : '';
    this.setReconnecting(false);
    this.setLocked(false);
    this.buildDots();
    this.refreshVehicle(false);
    this.refreshFoot();
    this.statusEl.textContent = 'Connecting…';

    if (!this.stage) this.stage = new LobbyStage(this.stageHost);
    this.field = -1; // force a seat rebuild
    this.setField(opts.fieldSize ?? 0);
    this.stage.show();
    this.root.style.display = '';
    requestAnimationFrame(() => {
      this.stage?.resize();
      this.layoutStage();
    });
  }

  /** M64: the matchmaker is searching while the player sits IN the lobby. */
  setSearching(on: boolean): void {
    this.searching = on;
    this.searchFound = Math.max(1, this.searchFound);
    this.renderSeats();
    this.refreshStatus();
  }

  /** Live "racers found" count from the matchmaker (monotonic theater). */
  setSearchCount(found: number): void {
    this.searchFound = Math.max(this.searchFound, found);
    if (this.searching) {
      this.renderSeats();
      this.refreshStatus();
    }
  }

  /** M140: a public crew room's search state (from the roster). */
  setCrewSearch(state: 'off' | 'on' | 'done', msLeft = 0): void {
    if (this.opts?.mode !== 'crew') return;
    this.crewSearch = state;
    if (state === 'on') {
      this.crewSearchEnds = Date.now() + Math.max(0, msLeft);
      if (this.crewTick === null) {
        this.crewTick = window.setInterval(() => {
          if (this.crewSearch !== 'on' || !this.isVisible) this.stopCrewTick();
          this.refreshStatus();
        }, 250);
      }
    } else {
      this.stopCrewTick();
    }
    this.renderSeats();
    this.refreshFoot();
    this.refreshStatus();
  }

  private stopCrewTick(): void {
    if (this.crewTick !== null) {
      window.clearInterval(this.crewTick);
      this.crewTick = null;
    }
  }

  /** M140: the empty seats a FIND RACERS would offer to strangers. */
  openSeats(): number {
    return Math.max(0, this.field - this.filledCount);
  }

  /** M64: matched — the room is known; stay in place (keep pick/lock state). */
  enterMatched(update: { roomKey: string; info?: string }): void {
    if (!this.opts) return;
    this.opts = { ...this.opts, roomKey: update.roomKey, info: update.info ?? this.opts.info };
    if (update.info) this.infoEl.textContent = update.info;
    this.searching = false;
    this.statusEl.textContent = 'Match found. Racers joining…';
    this.renderSeats();
  }

  /** The ride currently highlighted (locked or not) — the join URL carries it. */
  currentVehicle(): string {
    return this.selectedVehicle();
  }

  /** The grid size (from create config or the welcome). Rebuilds seat columns. */
  setField(n: number): void {
    if (n === this.field && this.seatCols.length === n) return;
    this.field = n;
    this.stage?.setField(Math.max(0, n));
    this.seatsRow.replaceChildren();
    this.seatCols = [];
    for (let i = 0; i < n; i++) {
      const col = el('div', 'racelobby-seat', this.seatsRow);
      const num = el('div', 'racelobby-seat-num', col);
      const name = el('div', 'racelobby-seat-name', col);
      const veh = el('div', 'racelobby-seat-veh', col);
      const state = el('div', 'racelobby-seat-state', col);
      this.seatCols.push({ col, num, name, veh, state });
      // M156: tap another racer's name → their REPORT / BLOCK card.
      const idx = i;
      name.addEventListener('click', () => {
        const raw = this.seatRawNames[idx];
        if (raw) this.cb.onPlayerName?.(raw);
      });
    }
    this.renderSeats();
    this.layoutStage();
  }

  /** Our server-assigned slot (from the welcome). Re-orders the columns so WE
   *  are the hero column on the left. */
  setLocalSlot(slot: number): void {
    if (this.localSlot !== slot) {
      this.localSlot = slot;
      this.stage?.setLocalSlot(slot);
      this.renderSeats();
      this.layoutStage();
    }
    if (this.statusEl.textContent === 'Connecting…') this.refreshStatus();
  }

  /** The room's live roster — seats fill/empty, rides turn, READY lights up. */
  setRoster(seats: LobbySeat[], startRequested: boolean): void {
    this.startRequested = startRequested;
    this.lastSeats = seats;
    this.filledCount = seats.filter((s) => s.filled).length;
    if (seats.length !== this.field) this.setField(seats.length);
    this.renderSeats();
    this.refreshFoot();
    this.refreshStatus();
  }

  setInfo(text: string): void {
    this.infoEl.textContent = text;
  }

  /** M142 review: the room's welcome says whether it really is a tour (the
   *  club invite only says what the starter asked for). */
  setTour(tour: boolean): void {
    if (!this.opts || !!this.opts.tour === tour) return;
    this.opts = { ...this.opts, tour };
    this.paintTitle();
    this.refreshFoot();
  }

  private paintTitle(): void {
    const opts = this.opts;
    if (!opts) return;
    // M142: a club race can be a club TOUR.
    this.titleEl.textContent =
      opts.mode === 'crew'
        ? opts.tour
          ? 'CLUB TOUR'
          : 'CLUB RACE'
        : opts.tour
          ? 'TOUR LOBBY'
          : 'RACE LOBBY';
  }

  setReconnecting(on: boolean): void {
    this.reconnectEl.style.display = on ? '' : 'none';
  }

  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  // ----- rendering -----------------------------------------------------------

  /** Visual column order: OUR seat first (the hero), then the rest ascending. */
  private visualOrder(): number[] {
    const local = this.localSlot >= 0 ? this.localSlot : 0;
    const order = [local];
    for (let s = 0; s < this.field; s++) if (s !== local) order.push(s);
    return order;
  }

  /** Repaint every seat column + the 3D seats from roster/local/search state. */
  private renderSeats(): void {
    if (this.field <= 0) return;
    const local = this.localSlot >= 0 ? this.localSlot : 0;
    const order = this.visualOrder();
    const bySlot = new Map<number, LobbySeat>();
    for (const s of this.lastSeats) bySlot.set(s.slot, s);

    this.seatRawNames = [];
    order.forEach((slot, visIdx) => {
      const ui = this.seatCols[visIdx];
      if (!ui) return;
      this.seatRawNames[visIdx] = null;
      ui.name.classList.remove('racelobby-seat-name-tap');
      const mine = slot === local;
      const seat = bySlot.get(slot);
      const filled = mine || !!seat?.filled;
      // Search theater: the found-count lights columns up before the room
      // roster exists (mine is always the first "found").
      const searchFound = this.searching && visIdx < this.searchFound;

      ui.col.classList.toggle('me', mine);
      ui.col.classList.toggle('filled', filled || searchFound);
      ui.num.textContent = String(visIdx + 1).padStart(2, '0');
      if (mine) {
        ui.name.textContent = 'YOU';
        ui.veh.textContent = vehicleLabel(this.selectedVehicle()).toUpperCase();
        ui.veh.style.color = ACCENTS[this.selectedVehicle()] ?? '#ff4438';
        ui.state.textContent = this.locked ? '✓ READY' : '';
        ui.state.classList.toggle('ready', this.locked);
        this.stage?.setSeat(
          slot,
          this.selectedVehicle(),
          this.locked,
          this.cb.lookFor(this.selectedVehicle()),
        );
      } else if (seat?.filled) {
        // M110: a REAL player's Racer ID name when the room relayed one; the
        // deterministic pool handle otherwise (AI seats, nameless guests).
        // M156: a blocked name never shows; every name is tappable (report /
        // block) — AI seats exactly like people, so nothing is revealed.
        const raw = seat.name || onlineRacerName(slot, local, this.opts?.roomKey ?? '');
        this.seatRawNames[visIdx] = raw;
        ui.name.textContent = displayRacerName(raw).toUpperCase();
        ui.name.classList.add('racelobby-seat-name-tap');
        ui.veh.textContent = vehicleLabel(seat.vehicle).toUpperCase();
        ui.veh.style.color = ACCENTS[seat.vehicle] ?? '#9fb0c8';
        ui.state.textContent = seat.ready ? '✓ READY' : '· PICKING';
        ui.state.classList.toggle('ready', seat.ready);
        this.stage?.setSeat(slot, seat.vehicle, seat.ready, seat.look ?? '');
      } else if (searchFound) {
        ui.name.textContent = 'RACER FOUND';
        ui.veh.textContent = '';
        ui.state.textContent = '· JOINING';
        ui.state.classList.remove('ready');
        this.stage?.setSeat(slot, '', false);
      } else {
        ui.name.textContent =
          this.searching || this.crewSearch === 'on'
            ? 'SEARCHING…'
            : this.opts?.mode === 'matchmade' || this.crewSearch === 'done'
              ? 'JOINING…'
              : 'WAITING…';
        ui.veh.textContent = '';
        ui.state.textContent = '';
        ui.state.classList.remove('ready');
        this.stage?.setSeat(slot, '', false);
      }
    });
    // M156: REPORT A RACER shows once anyone else is in the room.
    this.reportLink.style.display = this.seatRawNames.some(Boolean) ? '' : 'none';
  }

  /** Feed the header columns' measured centres to the 3D stage so every car
   *  lands under its plate — at any window shape. The hero row sits lower on
   *  screen (nearer the camera = bigger); the rest recede up and away. */
  private layoutStage(): void {
    if (!this.stage || this.field <= 0) return;
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (w <= 0 || h <= 0) return;
    const portrait = h > w;
    const short = h < 520; // landscape phones: a thin band between header + bar
    const order = this.visualOrder();
    const targets: (SeatTarget | null)[] = new Array(this.field).fill(null);
    const m = Math.max(1, this.field - 1);
    order.forEach((slot, visIdx) => {
      const rect = this.seatCols[visIdx]?.col.getBoundingClientRect();
      let xFrac =
        rect && rect.width > 0 ? (rect.left + rect.width / 2) / w : (visIdx + 0.5) / this.field;
      let yFrac: number;
      if (visIdx === 0) {
        // The hero — big, front-left, pulled INBOARD of its header column so
        // the car body never clips the screen edge (owner note, M64b).
        xFrac = Math.max(xFrac, portrait ? 0.3 : short ? 0.21 : 0.19);
        yFrac = short ? 0.64 : portrait ? 0.62 : 0.76;
      } else if (portrait) {
        // M65 (owner): PORTRAIT opponents cascade up-and-right — each one a
        // step BEHIND and BESIDE the previous, so every car stays visible on
        // a phone held vertically (a same-depth row hid them behind each
        // other in the narrow view).
        const t = (visIdx - 1) / Math.max(1, this.field - 2);
        xFrac = 0.42 + 0.48 * t;
        yFrac = 0.57 - 0.13 * t;
      } else if (short) {
        yFrac = 0.5; // far + small: the field parks in the stage band, clear of the UI
      } else {
        yFrac = 0.62 - (0.12 * (visIdx - 1)) / m;
      }
      targets[slot] = { xFrac, yFrac };
    });
    this.stage.applyTargets(targets);
  }

  // ----- local seat (cycling + lock) ----------------------------------------

  private selectedVehicle(): string {
    return this.opts?.vehicles[this.selIndex] ?? 'coupe';
  }

  private cycle(dir: number): void {
    if (this.locked || !this.opts) return;
    const n = this.opts.vehicles.length;
    this.selIndex = (this.selIndex + dir + n) % n;
    this.refreshVehicle(true);
  }

  private lockIn(): void {
    if (this.locked) return;
    this.setLocked(true);
    sfxCollect();
    this.renderSeats();
    this.refreshFoot();
    this.refreshStatus();
    this.cb.onLockIn(this.selectedVehicle());
  }

  private setLocked(locked: boolean): void {
    this.locked = locked;
    this.prevBtn.disabled = locked;
    this.nextBtn.disabled = locked;
    this.lockBtn.style.display = locked ? 'none' : '';
    this.lockedChip.style.display = locked ? '' : 'none';
    this.statsPanel.classList.toggle('locked', locked);
  }

  private refreshVehicle(announce: boolean): void {
    const id = this.selectedVehicle();
    const accent = ACCENTS[id] ?? '#ff4438';
    this.vehNameEl.textContent = vehicleLabel(id).toUpperCase();
    this.vehNameEl.style.color = accent;
    this.vehNameEl.style.textShadow = `0 0 18px ${accent}66, 0 2px 10px rgba(0,0,0,.6)`;
    this.statsPanel.style.setProperty('--veh-accent', accent);
    const v = tuning.vehicles[id];
    STAT_BARS.forEach((s, i) => {
      const fill = v ? Math.min(1, Math.max(0.06, (v[s.key] - s.min) / (s.max - s.min))) : 0;
      const row = this.statsRows[i];
      if (row) row.fill.style.width = `${Math.round(fill * 100)}%`;
    });
    [...this.dotsEl.children].forEach((d, i) => d.classList.toggle('on', i === this.selIndex));
    this.renderSeats();
    if (announce) this.cb.onCycleVehicle(id);
  }

  private buildDots(): void {
    this.dotsEl.replaceChildren();
    for (let i = 0; i < (this.opts?.vehicles.length ?? 0); i++) {
      const d = el('span', 'racelobby-dot', this.dotsEl);
      d.addEventListener('click', () => {
        if (this.locked) return;
        this.selIndex = i;
        this.refreshVehicle(true);
      });
    }
  }

  // ----- footer / status ------------------------------------------------------

  private refreshFoot(): void {
    const mode = this.opts?.mode;
    // M140: an OPEN crew race starts through FIND RACERS (one racer is
    // enough — strangers and AI fill the rest); a crew-only race starts like
    // a friend room.
    if (mode === 'crew' && this.opts?.crewPublic) {
      this.startBtn.style.display = this.locked && this.crewSearch !== 'done' ? '' : 'none';
      this.startBtn.disabled = this.crewSearch !== 'off';
      this.startBtn.textContent = this.crewSearch === 'on' ? 'FINDING RACERS…' : 'FIND RACERS ›';
      return;
    }
    const friend = mode === 'friend' || mode === 'crew';
    const startable = friend && this.filledCount >= LOBBY_MIN_TO_START && !this.startRequested;
    this.startBtn.style.display = friend && this.locked ? '' : 'none';
    this.startBtn.disabled = !startable;
    this.startBtn.textContent = this.startRequested
      ? 'STARTING WHEN ALL READY…'
      : this.opts?.tour
        ? 'START TOUR ›'
        : 'START RACE ›';
  }

  private refreshStatus(): void {
    if (!this.opts) return;
    // M142: "race" or "tour" in every line that names what is starting.
    const what = this.opts.tour ? 'tour' : 'race';
    if (this.searching) {
      const total = this.field > 0 ? this.field : 6;
      this.statusEl.textContent = `Searching for racers near you…  ${Math.min(this.searchFound, total)} / ${total}`;
      return;
    }
    if (this.opts.mode === 'crew' && this.opts.crewPublic && this.crewSearch !== 'done') {
      if (this.crewSearch === 'on') {
        const s = Math.max(0, Math.ceil((this.crewSearchEnds - Date.now()) / 1000));
        this.statusEl.textContent = `Finding online racers for the empty seats…  0:${String(s).padStart(2, '0')}`;
      } else if (!this.locked) {
        this.statusEl.textContent =
          'Cycle your ride and LOCK IN. Your club can join from their invite.';
      } else {
        this.statusEl.textContent =
          this.filledCount <= 1
            ? 'Your club got an invite. Press FIND RACERS when you want to go.'
            : 'Ready. FIND RACERS fills the empty seats with online racers.';
      }
      return;
    }
    if (this.opts.mode === 'matchmade' || this.crewSearch === 'done') {
      this.statusEl.textContent = this.locked
        ? 'Waiting for the other racers to lock in…'
        : `Cycle your ride and LOCK IN. The ${what} starts when everyone is ready.`;
      return;
    }
    if (this.filledCount <= 1) {
      // A club race's invites already went out; a friend room shares its code.
      this.statusEl.textContent =
        this.opts.mode === 'crew'
          ? 'Your club got an invite. They join this lobby live.'
          : 'Share the code or link. Friends join this lobby live.';
    } else if (this.startRequested) {
      this.statusEl.textContent = this.locked
        ? 'Start requested. Waiting for everyone to lock in…'
        : `Start requested. LOCK IN to launch the ${what}!`;
    } else {
      this.statusEl.textContent = this.locked
        ? 'Ready. Press START once everyone is set.'
        : 'Cycle your ride and LOCK IN.';
    }
  }

  private async copy(): Promise<void> {
    try {
      // M111: native Clipboard plugin first (WKWebView clipboard quirks).
      if (nativeShims.copyText) await nativeShims.copyText(this.link);
      else await navigator.clipboard.writeText(this.link);
      this.copyBtn.textContent = 'COPIED!';
      this.clearCopyTimer();
      this.copyResetTimer = window.setTimeout(() => {
        this.copyBtn.textContent = 'COPY INVITE LINK';
      }, 1600);
    } catch {
      this.copyBtn.textContent = 'COPY FAILED. SHARE THE CODE';
    }
  }

  private clearCopyTimer(): void {
    if (this.copyResetTimer !== null) {
      window.clearTimeout(this.copyResetTimer);
      this.copyResetTimer = null;
    }
  }

  hide(): void {
    this.clearCopyTimer();
    this.stopCrewTick();
    this.stage?.hide();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.clearCopyTimer();
    this.unsubViewport?.();
    this.unsubBlocked?.();
    this.stage?.dispose();
    this.root.remove();
  }
}
